import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import "./BlogShowcasePage.css";
import { Helmet } from "react-helmet-async";

export default function BlogShowcasePage() {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const [topAuthors, setTopAuthors] = useState([]);
  const [popularIndex, setPopularIndex] = useState(0);
  const [recentPage, setRecentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const featuredGridRef = useRef(null);

  // Helper function to truncate description
  const truncateDescription = (description, wordLimit = 7) => {
    const cleanText = description.replace(/<[^>]+>/g, '').replace(/&[^;]+;/g, '');
    const words = cleanText.trim().split(/\s+/);
    return words.slice(0, wordLimit).join(' ') + (words.length > wordLimit ? '...' : '');
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchBlogs = async () => {
      try {
        const response = await fetch(
          "https://aikyam-server-supabaseoff.onrender.com/api/blogs",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch blogs");
        }

        const data = await response.json();
        const uniqueAuthors = [
          ...new Set(data.map((blog) => blog.author || "Anonymous")),
        ].slice(0, 3);

        const categoryCounts = data.reduce((acc, blog) => {
          const category = blog.category || "General";
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {});
        const formattedCategories = Object.entries(categoryCounts).map(([name, count]) => ({
          name,
          count,
        }));

        const formattedBlogs = data.map((blog, index) => ({
          id: blog.id || index,
           slug: blog.slug,
          title: blog.title || "Untitled",
          category: blog.category || "General",
          author: blog.author || "Anonymous",
        date: blog.created_at
          ? new Date(blog.created_at).toLocaleDateString("en-US", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })
          : "No Date",  
          time: blog.duration ? `${blog.duration} Min. to Read` : "3 Min. to Read",
          description: blog.description ? truncateDescription(blog.description) : "No description",
          image: blog.image
            ? blog.image.trim().replace(/\s/g, "%20")
            : "https://placehold.co/300x200?text=No+Image",
        }));

        setBlogs(formattedBlogs);
        setFilteredBlogs(formattedBlogs);
        setTopAuthors(uniqueAuthors);
        setCategories(formattedCategories);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  useEffect(() => {
    const grid = featuredGridRef.current;
    if (grid) {
      grid.style.scrollBehavior = "smooth";
    }
  }, [blogs]);

  useEffect(() => {
    if (activeTag === null) {
      setFilteredBlogs(blogs);
    } else {
      const filtered = blogs.filter((blog) => blog.category === activeTag);
      setFilteredBlogs(filtered);
    }
    setRecentPage(1);
    setPopularIndex(0);
  }, [activeTag, blogs]);

  const postsPerPage = 2;
  const recentPostsPerPage = 5;
  const totalSets = Math.ceil(filteredBlogs.length / postsPerPage);
  const totalRecentPages = Math.ceil(filteredBlogs.length / recentPostsPerPage);

  const handlePrev = () => {
    setPopularIndex((prev) => (prev === 0 ? totalSets - 1 : prev - 1));
  };

  const handleNext = () => {
    setPopularIndex((prev) => (prev === totalSets - 1 ? 0 : prev + 1));
  };

  const handleRecentPrev = () => {
    setRecentPage((prev) => (prev === 1 ? totalRecentPages : prev - 1));
  };

  const handleRecentNext = () => {
    setRecentPage((prev) => (prev === totalRecentPages ? 1 : prev + 1));
  };

  const handlePageClick = (page) => {
    setRecentPage(page);
  };

  if (loading) {
    return (
      <div className="blogsp-main-container">
        <div className="loading-box">
          <div className="loading-spinner"></div>
          <p>Loading....</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-page">
        <img src="/SVG/eror.svg" alt="Error" className="error-image" />
        <Link to="/" className="error-home-button">Go to Home Page</Link>
      </div>
    );
  }

  const recentBlogs = filteredBlogs.slice(
    (recentPage - 1) * recentPostsPerPage,
    recentPage * recentPostsPerPage
  );
  const pageNumbers = Array.from({ length: totalRecentPages }, (_, i) => i + 1);

  return (
        <>
      <Helmet>
        <title>Sports Science & Athletic Performance Blog | Aikyam Sports</title>
        <meta
          name="description"
          content="Read the latest from Aikyam Sports on athlete performance tracking, physiotherapy, timing gate technology, and sports science — expert insights from Udupi's leading sports science centre."
        />
        <meta
          name="keywords"
          content="Aikyam Sports blog, athlete performance tracking, physiotherapy Udupi, sports science blog, timing gates, sports app India"
        />
        <link rel="canonical" href="https://aikyamsports.com/blog" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Sports Science & Athletic Performance Blog | Aikyam Sports" />
        <meta
          property="og:description"
          content="Expert articles on physiotherapy, performance tracking, timing gate technology, and sports science from Aikyam Sports, Udupi."
        />
        <meta property="og:url" content="https://aikyamsports.com/blog" />
        <meta property="og:image" content="https://img.youtube.com/vi/mkshyk__zSY/maxresdefault.jpg" />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sports Science & Athletic Performance Blog | Aikyam Sports" />
        <meta
          name="twitter:description"
          content="Latest insights on athlete performance tracking, physiotherapy, and sports science from Aikyam Sports, Udupi."
        />
        <meta name="twitter:image" content="https://img.youtube.com/vi/mkshyk__zSY/maxresdefault.jpg" />

        {/* Blog listing structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Aikyam Sports Blog",
            "url": "https://aikyamsports.com/blog",
            "description":
              "Articles on athlete performance tracking, physiotherapy, sports science, and timing gate technology from Aikyam Sports, Udupi.",
            "publisher": {
              "@type": "Organization",
              "name": "Aikyam Sports",
              "url": "https://aikyamsports.com",
            },
            "blogPost": filteredBlogs.slice(0, 10).map((b) => ({
              "@type": "BlogPosting",
              "headline": b.title,
              "url": `https://aikyamsports.com/blog/${b.slug}`,
              "datePublished": b.date,
              "author": {
                "@type": "Person",
                "name": b.author,
              },
            })),
          })}
        </script>

        {/* Featured video structured data (from sitemap) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": "INDIA's FIRST TIMING GATES! Measure Speed Like Never Before!!",
            "description":
              "Discover India's first wireless combined timing gate system designed to measure speed, power, and athletic performance with elite precision.",
            "thumbnailUrl": "https://img.youtube.com/vi/mkshyk__zSY/maxresdefault.jpg",
            "embedUrl": "https://www.youtube.com/embed/mkshyk__zSY",
            "uploadDate": "2025-01-01",
          })}
        </script>
      </Helmet>
    <div className="blogsp-main-container">
      <div className="blogsp-left-content">
        <h2>
          <span className="blogsp-highlightf">Featured</span> This Month
        </h2>
        <div className="blogsp-featured-grid-scroll" ref={featuredGridRef}>
          {filteredBlogs.slice(0, 4).map((item, i) => (
            <Link to={`/blog/${item.slug}`} className="blogsp-featured-card" key={i}>
              <span className="blogsp-tag">{item.category}</span>
              <h3>{item.title}</h3>
              <div
                className="blogsp-image-box"
                style={{
                  backgroundImage: `url(${item.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <p className="blogsp-meta">
                {item.author}   |📆 <span>{item.date}</span>   |🕑{" "}
                <span>{item.time}</span>
              </p>
              <p className="blogsp-description">{item.description}</p>
            </Link>
          ))}
        </div>

        <div className="blogsp-recent">
          <h2>
            <span className="blogsp-highlightf">Recently</span> Posted
          </h2>
          {recentBlogs.map((item, i) => (
            <Link to={`/blog/${item.slug}`} className="blogsp-scroll-card" key={i}>
              <div
                className="blogsp-image-box"
                style={{
                  backgroundImage: `url(${item.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="blogsp-scroll-text">
                <span className="blogsp-tag">{item.category}</span>
                <h3>{item.title}</h3>
                <p className="blogsp-meta">
                  {item.author}   | 📆 {item.date}   | 🕑 {item.time}
                </p>
                <p className="blogsp-description">{item.description}</p>
              </div>
            </Link>
          ))}
        </div>

        <div className="blogsp-pagination">
          <button onClick={handleRecentPrev}>← Prev</button>
          {pageNumbers.map((page) => (
            <button
              key={page}
              className={recentPage === page ? "active" : ""}
              onClick={() => handlePageClick(page)}
            >
              {page}
            </button>
          ))}
          <button className="blogsp-next" onClick={handleRecentNext}>Next →</button>
        </div>
      </div>

      <div className="blogsp-right-sidebar">
        <div className="blogsp-popular-box">
          <h2>
            <span className="blogsp-popular-label">Popular</span> Posted
          </h2>
          <div className="blogsp-popular-container">
            {filteredBlogs
              .slice(popularIndex * postsPerPage, (popularIndex + 1) * postsPerPage)
              .map((item, index) => (
                <Link to={`/blog/${item.slug}`} className="blogsp-popular-card" key={index}>
                  <span className="blogsp-tag">{item.category}</span>
                  <h3>{item.title}</h3>
                  <div className="blogsp-meta-popular">
                    <span>{item.author}</span>|<span>📅 {item.date}</span>|
                    <span>⏱ {item.time}</span>
                  </div>
                  <p className="blogsp-description">{item.description}</p>
                </Link>
              ))}
          </div>
          <div className="blogsp-popular-nav">
            <button onClick={handlePrev} className="blogsp-nav-btn">
              ← Prev
            </button>
            <button onClick={handleNext} className="blogsp-nav-btn blogsp-next">
              Next →</button>
          </div>
        </div>

        <div className="blogsp-authors-box">
          <h2>
            <span className="blogsp-highlightf">Top</span> Authors
          </h2>
          {topAuthors.map((author, index) => (
            <div className="blogsp-author-card" key={index}>
              <div className="blogsp-author-avatar" />
              <div className="blogsp-author-info">
                <h5>{author}</h5>
                
                <div className="blogsp-social-icons">
                  <i className="fab fa-facebook-f"></i>
                  <i className="fab fa-twitter"></i>
                  <i className="fab fa-instagram"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

<div className="blogsp-ad-box">
  <h4>
    Unlock Your
    <br />
    Athletic Potential
  </h4>
  <p>
    Discover expert insights on sports performance, physiotherapy,
    injury prevention, rehabilitation, and science-backed
    strategies to help you perform at your best.
  </p>
  <Link to="/fitness" className="blogsp-visit-btn">
    Our Best Services
  </Link>
</div>

        <div className="blogsp-category-box">
          <h2>
            <span className="blogsp-highlightf">Categories</span>
          </h2>
          <ul>
            {categories.map((category, index) => (
              <li key={index}>
                {category.name} <span>{category.count.toString().padStart(2, "0")}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="blogsp-update-box">
          <h2><span className="blogsp-highlightf">Today’s</span> Update</h2>
          <div className="blogsp-update-grid">
            <div className="blogsp-update-card"><h3>14</h3><p>New Posts</p></div>
            <div className="blogsp-update-card"><h3>480</h3><p>Total Visitors</p></div>
            <div className="blogsp-update-card"><h3>29</h3><p>New Subscribers</p></div>
            <div className="blogsp-update-card"><h3>138</h3><p>Blog Read</p></div>
          </div>
        </div>

        <div className="blogsp-tag-box">
          <h2><span className="blogsp-highlightf">Search</span> With Tags</h2>
          <div className="blogsp-tags">
            {categories.map((category, index) => (
              <span
                key={index}
                className={`blogsp-tag ${activeTag === category.name ? "active" : ""}`}
                onClick={() => {
                  setActiveTag(category.name);
                  window.scrollTo(0, 0);
                }}
              >
                {category.name}
              </span>
            ))}
          </div>
        </div>

      </div>
    </div>
    </>
  );
}
